// Import necessary libraries
import create from 'zustand' // State management library
import produce from 'immer' // Immutable state updates
import { persist } from 'zustand/middleware' // Persistence for state
import { keys } from 'lodash' // Utility for object key operations
import { convertToInteger } from '../helpers/utils' // Custom utility for integer conversion
import {
    _BATCH_PRICE_KEY, // Constants for metadata keys
    _DISCOUNTED_TOTAL_KEY,
    _KEY,
    _PARENT_KEY,
} from '../helpers/checkout'
import { nanoid } from 'nanoid' // Library for generating unique IDs
import {
    isCampaignProduct,
    isCYOProduct,
    MAILERS_QUANTITY_METAKEY,
    noOfRecordsField,
    purchaseField,
    shouldAddPagesAndPrinting,
    shouldNotAddFields,
} from '../helpers/slug' // Utility for field validation

const realPennedEnvelopeFieldName = 'Real Penned Envelope'
const pagesAndPrintingFieldName = 'Pages & Printing'
export const pagesAndPrintingVariationErrorMessage =
    'Pages & Printing option is currently unavailable. Please contact support.'

// Constant used to track which main product an add-on is associated with
export const FORPRODUCTKEY = 'For Product'

/**
 * Gets additional products associated with a cart item
 * @param {Object} params - Input parameters
 * @param {Array} params.addOnProducts - List of available add-on products
 * @param {Object} params.item - The cart item to find add-ons for
 * @returns {Object} - Object containing add-on products
 */
export const getAdditionalProductForCartItem = ({ addOnProducts, item }) => {
    let products = {}

    // Helper function to process a found product
    // For shipping, key value would be quantity
    const handleFoundProduct = ({ key, foundProduct, item }) => {
        const resolvedVariation = resolveAddOnVariation({
            key,
            item,
            foundProduct,
        })
        const shouldResolveVariation =
            foundProduct.option_name === pagesAndPrintingFieldName

        if (shouldResolveVariation && !resolvedVariation) {
            return
        }

        const selectedOptionValue = getPersistedSelectedOptionValue({
            key,
            item,
            foundProduct,
        })

        products[key] = {
            product: foundProduct.product,
            variation: resolvedVariation,
            [FORPRODUCTKEY]: item.product.name, // Track parent product name
            option_value: foundProduct.option_name,
            option_name: key,
            [_BATCH_PRICE_KEY]: getAddOnUnitPrice({
                foundProduct,
                resolvedVariation,
            }),
            quantity: getItemQty({ key, item, foundProduct }),
        }

        if (selectedOptionValue) {
            products[key][key] = selectedOptionValue
        }
    }

    // Loop through all properties of the item
    for (const key of keys(item)) {
        // Try to find an add-on product where option_name matches the key
        let foundProduct = addOnProducts.find(
            (product) => product.option_name === key && item[key] !== false,
        )

        if (foundProduct) handleFoundProduct({ key, foundProduct, item })
        else {
            // Try to find an add-on product where option_name matches the value
            let foundProduct = addOnProducts.find(
                (product) => product.option_name === item[key],
            )
            if (foundProduct) handleFoundProduct({ key, foundProduct, item })
        }
    }

    return products
}

/**
 * Converts add-on products into line items for checkout
 * @param {Object} params - Input parameters
 * @param {Array} params.cart - Cart items
 * @param {Array} params.addOnProducts - Available add-on products
 * @returns {Array} - Line items for checkout
 */
export const getAdditionalProductLineItems = ({ cart, addOnProducts }) => {
    // Get add-on products for each cart item
    const products = cart.map((item) =>
        getAdditionalProductForCartItem({ addOnProducts, item }),
    )
    let lineItems = []

    // Convert products to line items format
    products.forEach((items) => {
        keys(items).forEach((key) => {
            const quantity = getAddOnQuantity(items[key])
            lineItems.push({
                product_id: items[key].variation?.id || items[key].product.id,
                quantity,
                _key: nanoid(), // Add unique key for API identification
                meta_data: [
                    {
                        key: FORPRODUCTKEY,
                        value: items[key][FORPRODUCTKEY],
                    },
                    {
                        key: _BATCH_PRICE_KEY,
                        value:
                            items[key][_BATCH_PRICE_KEY] ||
                            items[key].product.price,
                    },
                ],
            })
            if (items[key][key]) {
                lineItems[lineItems.length - 1].meta_data.push({
                    key,
                    value: items[key][key],
                })
            }
        })
    })
    return lineItems
}

/**
 * Calculates the total price of all add-on products
 * @param {Object} params - Input parameters
 * @param {Array} params.cart - Cart items
 * @param {Array} params.addOnProducts - Available add-on products
 * @returns {number} - Total price of add-on products
 */
export const getAdditionalProductsTotal = ({ cart, addOnProducts }) => {
    const products = cart.map((item) =>
        getAdditionalProductForCartItem({ addOnProducts, item }),
    )
    let total = 0

    // Calculate total by multiplying price by quantity for each add-on
    products.forEach((lineItem) => {
        keys(lineItem).forEach((key) => {
            total +=
                parseFloat(
                    lineItem[key][_BATCH_PRICE_KEY] ||
                        lineItem[key].product.price,
                ) * getAddOnQuantity(lineItem[key])
        })
    })
    return total
}

/**
 * Zustand store for add-on products
 * Persists data with store-specific suffix
 */
export const AddOnProducts = create(
    persist(
        (set) => ({
            products: [],
            // Save products to store
            save: (products) => {
                set(
                    produce((draft) => {
                        draft.products = products
                    }),
                )
            },
        }),
        {
            name: `olm-products-${process.env.NEXT_PUBLIC_STORE_SUFFIX}`,
        },
    ),
)

/**
 * Zustand store for add-on cart
 * Manages cart operations (add, remove, reset)
 * Persists data with store-specific suffix
 */
export const AddOnCartStore = create(
    persist(
        (set) => ({
            cart: [],
            // Add item to cart
            add: (item) => {
                set(
                    produce((draft) => {
                        draft.cart.push(item)
                    }),
                )
            },
            // Remove item by index
            remove: (idx) => {
                set(
                    produce((draft) => {
                        draft.cart.splice(idx, 1)
                        // removeCoupon() - commented out in original
                        // draft.coupon_lines = [] // remove coupon
                    }),
                )
            },
            // Remove all items associated with a product
            removeByProduct: (key) => {
                set(
                    produce((draft) => {
                        // console.log('removeByProduct', key)
                        draft.cart = draft.cart.filter(
                            (item) => item[_PARENT_KEY] !== key,
                        )
                    }),
                )
            },
            // Reset cart to empty
            reset: () => {
                set(
                    produce((draft) => {
                        draft.cart = []
                        // removeCoupon() - commented out in original
                    }),
                )
            },
            // Set cart to specific value
            setCart: (cart) => {
                set(
                    produce((draft) => {
                        draft.cart = cart
                    }),
                )
            },
        }),
        {
            name: `olm-addon-cart-${process.env.NEXT_PUBLIC_STORE_SUFFIX}`,
        },
    ),
)

// Definition for special add-on offer field
const AddOfferField = {
    name: 'Add Offer?',
}

const StreetViewField = {
    name: 'Add Streetview Image',
    paidOption: 'Yes (+$0.02)',
}

// Products that require special conditions to be added
export const specialConditionsProducts = [
    {
        name: AddOfferField.name,
    },
    {
        name: realPennedEnvelopeFieldName,
    },
    {
        name: StreetViewField.name,
        optionToMatch: StreetViewField.paidOption,
    },
]

/**
 * Determines if an add-on product should not be added based on conditions
 * @param {Object} params - Input parameters
 * @param {Object} params.item - Cart item
 * @param {Object} params.foundProduct - Found add-on product
 * @returns {boolean} - True if product should not be added
 */
export const shouldNotAddAdditionalProduct = ({ item, foundProduct }) => {
    const shouldAdd = shouldNotAddFields.every((product) => {
        if (
            foundProduct.option_name
                .toLowerCase()
                .includes(product.stringToMatch)
        ) {
            // Match key to specialConditionsProducts array name
            // Check if item[product.name] is true or "yes"
            return item?.[product.name] == true || item?.[product.name] == 'yes'
        }
        return false
    })

    return shouldAdd
}

/**
 * Determines if an add-on product should not be added based on selected option
 * @param {Object} params - Input parameters
 * @param {string} params.key - Option key (may be prefixed with "Mailer Type X ")
 * @param {Object} params.item - Cart item
 * @returns {boolean} - True if product should not be added
 */
export const shouldNotAddAdditionalProductBasedOnSelectOption = ({
    key,
    item,
    foundProduct,
}) => {
    if (
        foundProduct?.option_name === pagesAndPrintingFieldName &&
        (isCYOProduct(item?.product)
            ? !item?.[key]
            : !shouldAddPagesAndPrinting(item?.product))
    ) {
        return true
    }

    const shouldAdd = specialConditionsProducts.some((product) => {
        // For Multi Touch: key might be "Mailer Type 1 Upgrade to Real Penned Envelope?"
        // We check if it matches exactly OR ends with the product name
        const keyMatchesExact = key === product.name
        const keyMatchesFuzzy = key.endsWith(product.name)

        if (keyMatchesExact || keyMatchesFuzzy) {
            const selectedValue = item?.[key]

            // Special-case Add Offer: customer-provided offers should NOT add addon.
            if (
                product.name === AddOfferField.name &&
                selectedValue === 'Yes, use my offer values'
            ) {
                return true
            }

            if (!selectedValue?.startsWith('Yes')) {
                return true
            }
        }
        return false
    })

    return shouldAdd
}

/**
 * Gets item quantity based on product type
 * @param {Object} params - Input parameters
 * @param {string} params.key - Option key (may be prefixed with "Mailer Type X ")
 * @param {Object} params.item - Cart item
 * @param {Object} params.foundProduct - Found add-on product
 * @returns {number} - Item quantity
 */
const getItemQty = ({ key, item, foundProduct }) => {
    // For per-mailer addons with prefixed Multi Touch keys, use base quantity per mailer
    // item['quantity'] = total (e.g., 1500), item[MAILERS_QUANTITY_METAKEY] = base (e.g., 500)
    const isMultiTouchKey = key.includes('Mailer Type')
    const isPerMailerAddon =
        foundProduct.option_name === realPennedEnvelopeFieldName ||
        foundProduct.option_name === StreetViewField.name

    if (isPerMailerAddon && isMultiTouchKey) {
        return item[MAILERS_QUANTITY_METAKEY] || item['quantity']
    }

    // For per-mailer addons in simple products (no prefix), use appropriate quantity
    if (isPerMailerAddon) {
        return isCampaignProduct(item?.product)
            ? item[MAILERS_QUANTITY_METAKEY]
            : item['quantity']
    }

    // Add Offer always uses total campaign quantity
    if (foundProduct.option_name === AddOfferField.name) {
        return isCampaignProduct(item?.product)
            ? item[MAILERS_QUANTITY_METAKEY]
            : item['quantity']
    }

    if (foundProduct.option_name === pagesAndPrintingFieldName) {
        return isCampaignProduct(item?.product)
            ? item[MAILERS_QUANTITY_METAKEY]
            : item['quantity']
    }

    // Purchase list summary should use the entered record count, not boolean true/false
    if (foundProduct.option_name === purchaseField.name) {
        const manualCount = Number(item?.[noOfRecordsField.name] || 0)
        return manualCount > 0
            ? Math.max(manualCount, purchaseField.minQuantity)
            : item[key]
    }

    // Default: use the value stored in item[key]
    return item[key]
}

export const getAddOnQuantity = (addOn) => {
    return convertToInteger(addOn?.quantity)
}

/**
 * Gets add-on products to add to cart
 * @param {Object} item - Cart item
 * @returns {Array} - List of add-on products to add
 */
export const getAddOnToAddToCart = (
    item,
    {
        throwOnUnresolvedVariation = false,
        addOnProducts: providedAddOnProducts,
    } = {},
) => {
    // Get add-on products from store
    const addOnProducts =
        providedAddOnProducts || AddOnProducts.getState().products

    let products = []

    // Helper function to process a found product
    const handleFoundProduct = ({
        key,
        foundProduct,
        item,
        throwOnUnresolvedVariation = false,
    }) => {
        // Skip if product should not be added based on conditions
        if (shouldNotAddAdditionalProduct({ key, item, foundProduct })) return

        // Skip if product should not be added based on select option
        if (
            shouldNotAddAdditionalProductBasedOnSelectOption({
                key,
                item,
                foundProduct,
            })
        )
            return

        const resolvedVariation = resolveAddOnVariation({
            key,
            item,
            foundProduct,
            throwOnUnresolvedVariation,
        })
        const shouldResolveVariation =
            foundProduct.option_name === pagesAndPrintingFieldName

        if (shouldResolveVariation && !resolvedVariation) return

        const selectedOptionValue = getPersistedSelectedOptionValue({
            key,
            item,
            foundProduct,
        })

        // Create object for add-on product
        const foundObj = {
            name: key,
            [_KEY]: nanoid(), // Add unique key for API identification
            [_PARENT_KEY]: item[_KEY], // Link to parent product
            product: foundProduct.product,
            variation: resolvedVariation,
            [FORPRODUCTKEY]: item.product.name,
            option_value: foundProduct.option_name,
            option_name: key,
            [_BATCH_PRICE_KEY]: getAddOnUnitPrice({
                foundProduct,
                resolvedVariation,
            }),
            quantity: getItemQty({ item, key, foundProduct }),
        }

        if (selectedOptionValue) {
            foundObj[key] = selectedOptionValue
        }

        products.push(foundObj)
    }

    // Loop through all properties of the item
    for (const key of keys(item)) {
        // Try to find an add-on product where option_name matches the key (exact match)
        let foundProduct = addOnProducts.find(
            (product) => product.option_name === key && item[key] !== false,
        )

        // For Multi Touch products, field names are prefixed with "Mailer Type X "
        // Try fuzzy matching: check if key ends with the option_name
        if (!foundProduct) {
            foundProduct = addOnProducts.find(
                (product) =>
                    key.endsWith(product.option_name) && item[key] !== false,
            )
        }

        if (foundProduct) {
            // Ensure we respect special conditions (e.g., "Yes" vs "No" for select fields)
            if (
                shouldNotAddAdditionalProductBasedOnSelectOption({
                    key,
                    item,
                    foundProduct,
                })
            ) {
                foundProduct = null // Invalidate if conditions aren't met
            }
        }

        if (foundProduct)
            handleFoundProduct({
                key,
                foundProduct,
                item,
                throwOnUnresolvedVariation,
            })
        else {
            // Try to find an add-on product where option_name matches the value
            let foundProduct = addOnProducts.find(
                (product) => product.option_name === item[key],
            )
            if (foundProduct)
                handleFoundProduct({
                    key,
                    foundProduct,
                    item,
                    throwOnUnresolvedVariation,
                })
        }
    }

    // console.log('products== added', products) - commented out in original

    return products
}

// Commented out function call in original code
// const removeCoupon = () => (CartStore.getState().removeCoupon)()

/**
 * Formats add-on products for checkout
 * @returns {Array} - Line items for checkout
 */
export const getAddOnForCheckout = () => {
    // Get cart from store
    const products = AddOnCartStore.getState().cart
    let lineItems = []

    // Convert products to line items format for checkout
    products.forEach((item) => {
        const quantity = getAddOnQuantity(item)
        lineItems.push({
            product_id: item.variation?.id || item.product.id,
            quantity,
            meta_data: [
                {
                    key: FORPRODUCTKEY,
                    value: item[FORPRODUCTKEY],
                },
                {
                    key: _BATCH_PRICE_KEY,
                    value: item[_BATCH_PRICE_KEY] || item.product.price,
                },
                {
                    key: _DISCOUNTED_TOTAL_KEY,
                    value: item[_DISCOUNTED_TOTAL_KEY],
                },
            ],
        })
        if (item[item.option_name]) {
            lineItems[lineItems.length - 1].meta_data.push({
                key: item.option_name,
                value: item[item.option_name],
            })
        }
    })
    return lineItems
}

const getPersistedSelectedOptionValue = ({ key, item, foundProduct }) => {
    if (foundProduct.option_name !== pagesAndPrintingFieldName) return null
    return item?.[key] || null
}

const resolveAddOnVariation = ({
    key,
    item,
    foundProduct,
    throwOnUnresolvedVariation = false,
}) => {
    if (foundProduct.option_name !== pagesAndPrintingFieldName) return null

    const selectedValue = item?.[key]
    const variations = Array.isArray(foundProduct?.variations)
        ? foundProduct.variations
        : []

    const resolvedVariation = variations.find(({ optionValues }) =>
        Object.values(optionValues || {}).includes(selectedValue),
    )

    if (!resolvedVariation && throwOnUnresolvedVariation) {
        throw new Error(pagesAndPrintingVariationErrorMessage)
    }

    return resolvedVariation || null
}

const getAddOnUnitPrice = ({ foundProduct, resolvedVariation }) => {
    const price =
        resolvedVariation?.price ??
        resolvedVariation?.item?.price ??
        foundProduct?.product?.price ??
        0

    return String(price)
}
